<template>
  <v-container class="hat-product-wrapper">
    <!-- META-DATA -->
    <vue-headful
      :title="hat.name + ' | Hats' + this.$store.state.app.meta.titleSuffix"
      :description="`the MemerHats ${hat.name}: ${hat.description}`" />

    <!-- IMAGE COLUMN -->
    <div
      v-if="!loading"
      key="image-column"
      class="hat-images">
      <div class="product-badges">
        <span
          v-if="onSale"
          class="product-badges__sale primary">
          SALE!
        </span>
        <span
          v-if="!inStock"
          class="product-badges__sold-out info">
          SOLD OUT
        </span>
        <span
          v-if="lowQuantity"
          class="product-badges__low-quantity warning">
          only <span class="product-badges__quantity-counter">{{ displayInStock }}</span> remaining!
        </span>
      </div>

      <div class="hat-image__wrapper">
        <img
          v-for="image in hat.images"
          v-show="imageIndex === hat.images.indexOf(image)"
          :key="image"
          :src="imageDir + currentImagePath"
          class="hat-image">
      </div>
      <div class="hat-image-controls">
        <v-btn
          v-for="image in hat.images"
          :key="image.id"
          small
          icon
          :flat="imageIndex !== hat.images.indexOf(image)"
          color="accent"
          @click="setImageIndex(hat.images.indexOf(image))">
          <v-icon>stop</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- INFO COLUMN -->
    <div
      v-if="!loading"
      key="info-column"
      class="hat-info">
      <h2 class="hat-info__name">
        {{ hat.name }}
      </h2>
      <div class="hat-info__price-wrapper">
        <h3
          class="hat-info__retail-price"
          :class="{
            'hat-info__retail-price--sale': onSale

          }">
          {{ hat.retailPrice | price }}
        </h3>
        <h3
          v-if="onSale"
          class="hat-info__sale-price">
          {{ hat.salePrice | price }}
        </h3>
      </div>

      <v-divider />

      <div class="hat-info__buy-row">
        <v-btn
          :disabled="!inStock"
          large
          color="primary"
          class="hat-info__add-to-cart"
          @click="addToCart">
          {{ addToCartText }}
        </v-btn>
        <v-tooltip
          :disabled="this.$store.getters['auth/isAuthenticated']"
          bottom>
          <span>You must be logged in to save items to your favorites list</span>
          <v-btn
            slot="activator"
            flat
            large
            color="primary"
            :loading="favoritesLoading"
            :disabled="!this.$store.getters['auth/isAuthenticated']"
            class="hat-info__add-to-favorites"
            @click="setInFavorites">
            <v-icon>
              {{ hatInFavorites ? 'star' : 'star_border' }}
            </v-icon>
          </v-btn>
        </v-tooltip>
      </div>

      <v-divider />

      <div class="hat-info__description">
        {{ hat.description }}
      </div>
      <v-divider />
      <ul
        class="hat-info__details">
        <li class="hat-info__details-item">
          color: {{ hatColor }}
        </li>
        <li class="hat-info__details-item">
          style: {{ hatStyle }}
        </li>
      </ul>
      <div class="hat-info__shipping-row">
        Usually ships within 1 business day<br>
        Estimated delivery: 2-3 business days
      </div>

      <v-divider />

      <div class="hat-share">
        <!-- url copy hidden input -->
        <input
          :value="hatURL"
          class="hat-share__url-input">
        <v-btn
          flat
          color="primary"
          :loading="copyUrlLoading"
          :disabled="copyUrlLoading"
          @click="copyToClipboard">
          <span slot="loader">copied!</span>
          copy URL
          <v-icon>assignment</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <div class="related-hats">
        <h4
          v-if="hat.relatedIds"
          class="related-hats__header">
          related hats
        </h4>
        <ul class="related-hats__items-wrapper">
          <related-hat
            v-for="id in relatedHats"
            :key="id"
            :hat-slug="id"
            class="related-hats__item" />
        </ul>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

import RelatedHat from '../components/RelatedHat.vue';

export default {
  name: 'HatProduct',
  components: {
    RelatedHat,
  },
  data() {
    return {
      imageIndex: 0,
      favoritesLoading: false,
      copyUrlLoading: false,
    };
  },
  computed: {
    ...mapState('hats', {
      hats: 'hats',
      hat: 'hat',
      hatsConfig: 'hatsConfig',
      hatStyles: 'hatStyles',
      imageDir: 'imageDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
      lowQuantityThreshold: 'lowQuantityThreshold',
    }),
    displayInStock() {
      return this.hat.quantityInStock - this.hatsConfig.inventoryBuffer >= 0
        ? this.hat.quantityInStock - this.hatsConfig.inventoryBuffer
        : 0;
    },
    inStock() {
      return this.displayInStock > 0;
    },
    onSale() {
      return this.inStock && Boolean(this.hat.salePrice);
    },
    lowQuantity() {
      return this.inStock && this.displayInStock < this.lowQuantityThreshold;
    },
    currentImagePath() {
      return this.hat.images[this.imageIndex];
    },
    hatInFavorites() {
      if (this.$store.getters['auth/isAuthenticated']) {
        const favoritesStore = this.$store.state.user.currentUser.favorites;
        const itemIndex = favoritesStore.hats.indexOf(this.hat.slug);
        if (itemIndex >= 0) {
          return true;
        }
        return false;
      }
      return false;
    },
    addToCartText() {
      return this.inStock
        ? 'add to cart'
        : 'sold out';
    },
    hatStyle() {
      if (this.hat.attributes) {
        const styleData = this.hatStyles.find(
          style => style.value === this.hat.attributes.style,
        );
        return styleData.text;
      }
      return '';
    },
    hatColor() {
      if (this.hat.attributes) {
        return this.hat.attributes.color;
      }
      return '';
    },
    hatURL() {
      return window.location.href;
    },
    relatedHats() {
      const relatedHats = [];
      // verify hat is loaded; needed to avoid console errors
      if (this.hat) {
        // add related hats from data.
        if (this.hat.relatedIds) {
          for (let i = 0; i < this.hat.relatedIds.length; i++) {
            relatedHats.push(this.hat.relatedIds[i]);
          }
        }
        // add defaults
        if (this.hat.relatedIds) {
          for (let i = 0; i < this.hats.length; i++) {
            if (this.hat.slug !== this.hats[i].slug && !this.hat.relatedIds.includes(this.hats[i].slug)) {
              relatedHats.push(this.hats[i].slug);
            }
          }
        }
      }
      // only return 4
      return relatedHats.slice(0, 4);
    },
  },
  beforeRouteUpdate(to, from, next) {
    // update data when going to related hat:
    // uses same component so created() isn't called
    this.$store.dispatch('hats/getHatBySlug', to.params.slug);
    next();
  },
  created() {
    this.$store.dispatch('hats/getHatStyles');
    this.$store.dispatch('hats/getAllHats');
    this.$store.dispatch('hats/getHatsConfig');
    this.$store.dispatch('hats/getHatBySlug', this.$route.params.slug);
  },
  methods: {
    setImageIndex(index) {
      this.imageIndex = index;
    },
    copyToClipboard() {
      this.copyUrlLoading = true;
      const text = document.querySelector('.hat-share__url-input');
      text.select();
      document.execCommand('copy');
      setTimeout(() => (this.copyUrlLoading = false), 1000);
    },
    addToCart() {
      const { hat } = this;
      this.$store.dispatch('cart/addHatToCart', hat);
      this.$store.dispatch('app/openMenu', 'cartDrawer');
    },
    setInFavorites() {
      const itemSet = 'hats';
      const itemId = this.hat.slug;
      this.favoritesLoading = true;
      this.$store
        .dispatch('user/toggleItemInFavorites', { itemSet, itemId })
        .then(() => {
          if (this.hatInFavorites) {
            this.$store.dispatch('app/openMenu', 'favoritesDrawer');
          }
          this.favoritesLoading = false;
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.hat-product-wrapper
  display: flex
  flex-flow: row wrap
  justify-content: center
  width: 100%

  &__loading
    padding-top: 10rem
    padding-bottom: 10rem


// HAT IMAGES
.hat-images

  position: relative

  display: flex
  flex-flow: column nowrap
  flex: 1 1 0
  align-items: center
  justify-content: flex-start

  padding-right: $base-padding*.5
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding-right: 0

.hat-image
  max-width: 100%

// BADGES
.product-badges
  position: absolute
  top: 0
  left: -.1rem

  display: flex
  flex-flow: column nowrap
  align-items: flex-start
  justify-content: flex-start

  z-index: 1

  padding-top: $base-padding

  &__sale,
  &__low-quantity,
  &__sold-out
    margin-top: $base-padding*.5
    padding: $base-padding*.25 $base-padding*.5

    color: $white
    font-weight: bold
    font-size: 1rem

    // border-radius: $base-border-radius
    // bg color set in template
    // with vuetify theme

  &__quantity-counter
    margin-right: $base-padding*-.25
    margin-left: $base-padding*-.25
    padding: 0 $base-padding*.5
    font-style: italic
    color: $primary
    background-color: $white
    border-radius: $base-border-radius


// HAT INFO
.hat-info
  flex: 1 1 0
  padding-left: $base-padding*.5
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding-left: 0
  &__name
    font-size: 1.75rem

  &__price-wrapper
    display: flex
    flex: row nowrap
    align-items: flex-start
    justify-content: flex-start
    padding-bottom: $base-padding
  &__retail-price
    font-size: 1.5rem
    padding-right: $base-padding
    &--sale
      font-weight: $base-font-weight
      opacity: .5
      text-decoration: line-through
  &__sale-price
    font-size: 1.5rem
    padding-right: $base-padding
    color: $primary
    font-style: italic
    font-weight: $base-header-font-weight

  &__buy-row
    display: flex
    align-items: center
    justify-content: space-between
    padding-top: $base-padding
    padding-bottom: $base-padding
  &__add-to-cart
    display: flex
    flex: 1 1 100%
    margin-left: 0

  &__description
    padding-top: $base-padding
    padding-bottom: $base-padding

  &__details
    list-style: none
    padding-top: $base-padding
    padding-left: 0
  &__details-item
    font-size: $font-size--small
    text-transform: capitalize

  &__shipping-row
    display: flex
    flex-flow: row wrap
    align-items: center
    padding: $base-padding 0
    text-align: left
    font-size: .75rem
    font-style: italic
    opacity: .65


.hat-share
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  width: 100%
  color: $primary
  padding-right: $base-padding

  &__url-input
    width: 1px
    opacity: 0


.related-hats
  &__items-wrapper
    display: flex
    flex-flow: row nowrap
    align-items: flex-start
    justify-content: space-between
    padding: 0
  &__item
    flex: 0 1 25%

</style>
